import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { deletionBlocker, formatMoney, isLive, makeId, projectTotals, sortCurrencies, uninvoicedByCurrency, type BillingDetails, type Client, type InvoiceLanguage, type TaxLine } from "@otrack/domain";
import { Button, EditableText, Field, FieldError, IconButton, Menu, Segmented, Select } from "@otrack/ui";
import { Chip, convertTyped, currencyOptions, EmptyState, Monogram, MoneyField, PageHeader, Panel, rise, Stat } from "../components";
import { useApp, useDataset, useFactory } from "../context";
import { CLIENT_PICTURE_LIMITS, prepareLogo } from "../logo";
import { LogoPicker } from "../logo-picker";
import { byRecent, InvoiceRow, parsePercent, percentText, TERMS, withInstallmentsTogether } from "../invoice-list";
import { ProjectCard } from "../project-card";
import { href, navigate } from "../router";
import { formatDuration, formatMonth, monthRange } from "../time";

const ROUNDING = [
  { value: "", label: "Exact time" },
  { value: "6", label: "Up to 6 minutes", hint: "0.1 h" },
  { value: "15", label: "Up to 15 minutes", hint: "Quarter hour" },
  { value: "30", label: "Up to 30 minutes", hint: "Half hour" }
];

export function ClientPage({ id }: { id: string }) {
  const { now, act, notify, openNewProject, openNewInvoice } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const client = dataset.clients[id];
  if (!client || !isLive(client)) return <div className="ot-page">
    <PageHeader title="Client not found" back={{ label: "Projects", href: href({ page: "projects" }) }}/>
    <EmptyState icon="user" title="This client is gone">It may have been moved to Trash or never synced to this device.</EmptyState>
  </div>;
  const projects = Object.values(dataset.projects).filter((project) => isLive(project) && project.clientId === id).sort((a, b) => Number(a.archivedAt !== null) - Number(b.archivedAt !== null) || a.name.localeCompare(b.name));
  const currency = client.defaultCurrency ?? dataset.settings.defaultCurrency;

  const tracked = { all: 0, month: 0 };
  const earned = new Map<string, number>();
  for (const project of projects) {
    const all = projectTotals(dataset, project.id, now);
    tracked.all += all.tracked;
    tracked.month += projectTotals(dataset, project.id, now, monthRange(now)).tracked;
    if (all.earned) earned.set(all.currency, (earned.get(all.currency) ?? 0) + all.earned);
  }

  const trash = async () => {
    const blocker = deletionBlocker(dataset, "client", id);
    if (blocker) return notify(blocker);
    await act([factory.op("client", id, { deletedAt: Date.now() })], `Moved ${client.name} to Trash`);
    navigate({ page: "projects" });
  };

  return <div className="ot-page">
    <PageHeader
      back={{ label: "Projects", href: href({ page: "projects" }) }}
      eyebrow={<Monogram name={client.name} picture={client.picture} size={44}/>}
      title={client.name}
      subtitle={<div className="ot-chips">
        <Chip icon="folder">{projects.length} {projects.length === 1 ? "project" : "projects"}</Chip>
        <Chip icon="coins">{client.defaultHourlyRate !== null ? `${formatMoney({ amount: client.defaultHourlyRate, currency })}/h` : currency}</Chip>
        <Chip icon="receipt">Invoices in {client.invoiceLanguage === "es" ? "Spanish" : "English"}</Chip>
        {client.archivedAt && <Chip icon="archive" tone="muted">Archived</Chip>}
      </div>}
      actions={<>
        <Button icon="receipt" onClick={() => openNewInvoice(id)}>New invoice</Button>
        <Button icon="plus" onClick={() => openNewProject(id)}>New project</Button>
        <Menu label="Client actions" items={[
          { label: client.archivedAt ? "Unarchive" : "Archive", icon: "archive", onSelect: () => void act([factory.op("client", id, { archivedAt: client.archivedAt ? null : now })], client.archivedAt ? `Restored ${client.name}` : `Archived ${client.name}`) },
          { label: "Move to Trash", icon: "trash", tone: "danger", onSelect: () => void trash() }
        ]}/>
      </>}
    />
    <motion.div className="ot-kpis" {...rise(1)}>
      <Stat label="Tracked, all time" value={formatDuration(tracked.all)}/>
      <Stat label={`In ${formatMonth(now)}`} value={formatDuration(tracked.month)}/>
      {earned.size ? [...earned].map(([code, amount]) => <Stat key={code} label={`Earned, ${code}`} value={formatMoney({ amount, currency: code })} note="All time"/>) : <Stat label="Earned" value="—" note="Nothing billable yet"/>}
    </motion.div>
    <div className="ot-split">
      <div className="ot-split__main">
        <Panel title="Projects" icon="folder" index={2}>
          {projects.length === 0 ? <p className="ot-empty">No projects for this client yet.</p> : <div className="ot-pgrid ot-pgrid--tight">
            <AnimatePresence mode="popLayout">{projects.map((project, index) => <ProjectCard key={project.id} project={project} index={index}/>)}</AnimatePresence>
          </div>}
        </Panel>
        <ClientInvoices client={client}/>
        <BillingDetailsPanel client={client}/>
      </div>
      <aside className="ot-split__side"><ClientDetails client={client}/></aside>
    </div>
  </div>;
}

function useSaveClient(client: Client) {
  const { act } = useApp();
  const factory = useFactory();
  return (patch: Record<string, unknown>, message: string) => act([factory.op("client", client.id, patch)], message);
}

function ClientDetails({ client }: { client: Client }) {
  const dataset = useDataset();
  const save = useSaveClient(client);
  const currency = client.defaultCurrency ?? dataset.settings.defaultCurrency;
  const [pictureBusy, setPictureBusy] = useState(false);
  const [pictureError, setPictureError] = useState<string | null>(null);
  const uploadPicture = async (file: File) => {
    setPictureBusy(true);
    setPictureError(null);
    try {
      await save({ picture: await prepareLogo(file, CLIENT_PICTURE_LIMITS) }, "Picture saved");
    } catch (error) {
      setPictureError(error instanceof Error ? error.message : "That image could not be used.");
    } finally {
      setPictureBusy(false);
    }
  };

  return <Panel title="Details" icon="pencil" description="Changes save as you go." className="ot-panel--sticky" index={2}>
    <div className="ot-stack">
      <Field label="Picture" optional hint="A logo or photo, shown beside the name. PNG, JPEG, or WebP." className="ot-field--logo">
        <div className="ot-logo-field">
          <LogoPicker logo={client.picture} busy={pictureBusy} noun="picture" alt={`${client.name} picture`} onFile={(file) => void uploadPicture(file)} onRemove={() => { setPictureError(null); void save({ picture: null }, "Picture removed"); }}/>
          <FieldError>{pictureError}</FieldError>
        </div>
      </Field>
      <Field label="Name">
        <EditableText value={client.name} maxLength={200} onCommit={async (text) => {
          const name = text.trim();
          if (!name) return "Name the client.";
          if (name !== client.name) await save({ name }, `Renamed to ${name}`);
          return null;
        }}/>
      </Field>
      <Field label="Default currency" hint="Projects without their own currency use this.">
        <Select
          value={client.defaultCurrency ?? ""}
          options={[{ value: "", label: "Account default", hint: dataset.settings.defaultCurrency }, ...currencyOptions([client.defaultCurrency])]}
          onChange={(code) => {
            const next = code || dataset.settings.defaultCurrency;
            void save({ defaultCurrency: code || null, ...(next !== currency ? { defaultHourlyRate: convertTyped(client.defaultHourlyRate, currency, next) } : {}) }, `Currency set to ${next}`);
          }}
        />
      </Field>
      <Field label="Default hourly rate" hint="Used by this client's projects that have no rate of their own.">
        <MoneyField amount={client.defaultHourlyRate} currency={currency} perHour placeholder="None" onCommit={(defaultHourlyRate) => save({ defaultHourlyRate }, defaultHourlyRate === null ? "Rate cleared" : `Rate set to ${formatMoney({ amount: defaultHourlyRate, currency })}/h`)}/>
      </Field>
      <Field label="Invoice language">
        <Segmented<InvoiceLanguage>
          stretch
          value={client.invoiceLanguage}
          options={[{ value: "en", label: "English" }, { value: "es", label: "Spanish" }]}
          onChange={(invoiceLanguage) => void save({ invoiceLanguage }, `Invoices in ${invoiceLanguage === "es" ? "Spanish" : "English"}`)}
        />
      </Field>
      <Field label="Round time on invoices" hint="Stored time stays exact; each entry rounds up when invoicing.">
        <Select
          value={client.roundingMinutes === null ? "" : String(client.roundingMinutes)}
          options={ROUNDING}
          onChange={(value) => void save({ roundingMinutes: value ? Number(value) : null }, value ? `Rounding up to ${value} minutes` : "Rounding off")}
        />
      </Field>
      <Field label="Payment terms" hint="New invoices are due this long after they are issued.">
        <Select
          value={String(client.paymentTermDays)}
          options={TERMS}
          onChange={(value) => void save({ paymentTermDays: Number(value) }, `Terms set to ${TERMS.find((term) => term.value === value)?.label ?? value}`)}
        />
      </Field>
      <DefaultTaxes client={client}/>
    </div>
  </Panel>;
}

function BillingDetailsPanel({ client }: { client: Client }) {
  const save = useSaveClient(client);
  const detail = (field: keyof BillingDetails, label: string) => ({
    value: client.billingDetails[field],
    onCommit: async (text: string) => {
      const value = text.trim();
      if (value !== client.billingDetails[field]) await save({ billingDetails: { ...client.billingDetails, [field]: value } }, `${label} saved`);
    }
  });

  return <Panel title="Billing details" icon="id-card" description="Who invoices are addressed to." index={4}>
    <div className="ot-grid-2">
      <Field label="Legal name" optional><EditableText {...detail("name", "Legal name")} maxLength={200} placeholder={client.name}/></Field>
      <Field label="Email" optional><EditableText {...detail("email", "Email")} type="email" maxLength={320} placeholder="billing@example.com"/></Field>
      <Field label="Tax ID" optional hint="RIF, RUT, EIN, VAT…"><EditableText {...detail("taxId", "Tax ID")} maxLength={100}/></Field>
      <Field label="Address" optional className="ot-grid-2__wide"><EditableText {...detail("address", "Address")} multiline rows={3} maxLength={1000}/></Field>
    </div>
  </Panel>;
}

function ClientInvoices({ client }: { client: Client }) {
  const { now, openNewInvoice } = useApp();
  const dataset = useDataset();
  const invoices = withInstallmentsTogether(Object.values(dataset.invoices).filter((invoice) => isLive(invoice) && invoice.clientId === client.id).sort(byRecent));
  const uninvoiced = uninvoicedByCurrency(dataset, client.id);
  const owed = sortCurrencies(Object.keys(uninvoiced).filter((code) => uninvoiced[code]));
  const meta = owed.length ? <span className="ot-panel__meta">{owed.map((code, index) => <span key={code}>{index ? ", " : ""}<strong className="ot-mono">{formatMoney({ amount: uninvoiced[code]!, currency: code })}</strong></span>)} uninvoiced</span> : null;

  return <Panel title="Invoices" icon="receipt" aside={meta} index={3}>
    {invoices.length === 0
      ? <div className="ot-client-invoices__empty">
        <p className="ot-empty">{owed.length ? "Nothing invoiced yet. The work above is ready to bill." : "No invoices yet."}</p>
        {owed.length > 0 && <Button icon="receipt" onClick={() => openNewInvoice(client.id)}>New invoice</Button>}
      </div>
      : <div className="ot-ilist">{invoices.slice(0, 8).map((invoice, index) => <InvoiceRow key={invoice.id} invoice={invoice} dataset={dataset} now={now} showClient={false} index={index}/>)}</div>}
  </Panel>;
}

/** Tax lines copied onto every new invoice for the Client, like IVA and IGTF. */
function DefaultTaxes({ client }: { client: Client }) {
  const save = useSaveClient(client);
  const setTaxes = (defaultTaxes: TaxLine[], message: string) => save({ defaultTaxes }, message);
  const update = (tax: TaxLine, patch: Partial<TaxLine>, message: string) => setTaxes(client.defaultTaxes.map((other) => (other.id === tax.id ? { ...other, ...patch } : other)), message);
  return <div className="ot-taxes">
    <span className="ot-field__label">Taxes on new invoices</span>
    {client.defaultTaxes.length === 0 && <p className="ot-empty ot-taxes__none">None. Invoices start without tax.</p>}
    {client.defaultTaxes.map((tax) => <div className="ot-tax" key={tax.id}>
      <EditableText size="sm" value={tax.name} placeholder="IVA" maxLength={60} aria-label="Tax name" onCommit={(text) => { if (text.trim() !== tax.name) void update(tax, { name: text.trim() }, "Tax renamed"); }}/>
      <EditableText size="sm" className="ot-tax__rate" value={percentText(tax.rate)} inputMode="decimal" aria-label={`${tax.name || "Tax"} rate`} trailing={<span className="ot-unit">%</span>} onCommit={async (text) => {
        const rate = parsePercent(text);
        if (rate === undefined) return "0 to 100";
        if (rate !== tax.rate) await update(tax, { rate }, `${tax.name || "Tax"} set to ${percentText(rate)}%`);
        return null;
      }}/>
      <IconButton className="is-ghost" icon="close" label={`Remove ${tax.name || "tax"}`} tooltip={false} onClick={() => void setTaxes(client.defaultTaxes.filter((other) => other.id !== tax.id), "Tax removed")}/>
    </div>)}
    <div className="ot-taxes__actions">
      <Button tone="ghost" icon="plus" disabled={client.defaultTaxes.length >= 10} onClick={() => void setTaxes([...client.defaultTaxes, { id: makeId("tax"), name: "", rate: 0 }], "Tax added")}>Add tax</Button>
    </div>
  </div>;
}
